import { useCallback, useEffect, useMemo, useState } from 'react';
import { fmtDateTimeCST } from '../../utils/datetime';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PaperClipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { screeningApi } from '../../services/api';
import CompanyComparison from './CompanyComparison';
import {
  BID_FIT_LABELS,
  CATEGORY_LABELS,
  DECISION_CLASSES,
  DECISION_LABELS,
  OUTCOME_CLASSES,
  OUTCOME_LABELS,
  PRESCREEN_CLASSES,
  PRESCREEN_LABELS,
  VERDICT_LABELS,
  pickErrorMessage,
  type ScreeningOpportunity,
  type ScreeningOutcome,
} from '../../types/screening';

interface OpportunityDetailProps {
  opportunityId: number;
  onBack: () => void;
  onOpenBidWriting: (projectId?: number) => void;
}

const OUTCOME_OPTIONS: ScreeningOutcome[] = ['unknown', 'won', 'lost', 'abandoned'];

const formatDateTime = (value: string | null): string => {
  if (!value) return '未提供';
  return fmtDateTimeCST(value);
  };

/** 招标信息详情：公告要点 + 资质对比与决策（是否适合投标）+ 逐条判定复核 */
export default function OpportunityDetail({
  opportunityId,
  onBack,
  onOpenBidWriting,
}: OpportunityDetailProps) {
  const [data, setData] = useState<ScreeningOpportunity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 人工确认判定保存后的提示：结论需重新执行「资质对比与决策」才会刷新 */
  const [verdictNotice, setVerdictNotice] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [running, setRunning] = useState<'ingest' | null>(null);
  const [filter, setFilter] = useState<'all' | 'mandatory'>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [converting, setConverting] = useState(false);
  const [outcomeValue, setOutcomeValue] = useState<ScreeningOutcome>('unknown');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [outcomeSaving, setOutcomeSaving] = useState(false);
  /** 历史单企业评审记录：默认收起，仅在需要追溯时展开 */
  const [showLegacyEvaluation, setShowLegacyEvaluation] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await screeningApi.get(opportunityId);
      setData(res.data.data);
    } catch (err) {
      setError(pickErrorMessage(err, '加载招标信息详情失败'));
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setOutcomeValue(data?.outcome || 'unknown');
    setOutcomeNote(data?.outcome_note || '');
  }, [data?.id, data?.outcome, data?.outcome_note]);

  /** 历史单企业评审记录（单企业入口已下线，仅保留追溯展示） */
  const evaluation = data?.latest_evaluation || null;
  /** 最近一次「是否适合投标」审核摘要（与列表徽标、decision 同源） */
  const comparison = data?.latest_comparison || null;
  /** 头部得分：优先取审核批次里名次第 1 的企业得分，历史单企业评估仅作回退 */
  const headerScore = comparison?.best_total_score ?? evaluation?.total_score ?? null;

  // 本区块只保留「公告级」内容：要求文本、是否硬性门槛、分值、公告原文依据（对所有参评企业一致）。
  // 判定 / 证据 / 得分与人工确认统一在下方「资质对比与决策」按企业进行，避免两套判定口径并存。
  const requirements = useMemo(() => {
    const list = data?.requirements || [];
    if (filter === 'mandatory') {
      return list.filter((item) => item.is_mandatory);
    }
    return list;
  }, [data?.requirements, filter]);

  const mandatorySummary = useMemo(() => {
    const list = data?.requirements || [];
    return {
      total: list.length,
      mandatory: list.filter((item) => item.is_mandatory).length,
    };
  }, [data?.requirements]);

  /** 公告要求总数：统一以要求清单长度为准，避免依赖详情接口可能缺失的 requirement_count */
  const requirementCount = mandatorySummary.total;

  const runIngest = async () => {
    setRunning('ingest');
    setError(null);
    setLogs(['正在解析公告要点…']);
    try {
      await screeningApi.ingestStream(opportunityId, (event) => {
        if (event.error) {
          throw new Error(event.message || '要点抽取失败');
        }
        if (event.type === 'progress' && event.message) {
          setLogs((prev) => [...prev, event.message as string]);
        }
        if (event.type === 'result') {
          setLogs((prev) => [...prev, `已识别 ${event.requirements_count ?? 0} 条要求`]);
        }
      });
      await load();
    } catch (err) {
      setError(pickErrorMessage(err, '要点抽取失败'));
    } finally {
      setRunning(null);
    }
  };

  const handleMandatoryToggle = async (requirementId: number, isMandatory: boolean) => {
    try {
      await screeningApi.updateRequirement(requirementId, { is_mandatory: isMandatory });
      setVerdictNotice('门槛标记已保存：需重新执行下方「资质对比与决策」才会刷新整体结论。');
      await load();
    } catch (err) {
      setError(pickErrorMessage(err, '保存失败'));
    }
  };

  const handleConvert = async () => {
    if (!data) {
      return;
    }
    setConverting(true);
    setError(null);
    try {
      const res = await screeningApi.convertToProject(data.id);
      await load();
      window.alert(`${res.data.message}，已切换到「投标文件编写」模块`);
      onOpenBidWriting(res.data.project_id);
    } catch (err) {
      setError(pickErrorMessage(err, '转入投标文件编写失败'));
    } finally {
      setConverting(false);
    }
  };

  const handleSaveOutcome = async () => {
    if (!data) {
      return;
    }
    setOutcomeSaving(true);
    setError(null);
    try {
      await screeningApi.updateOutcome(data.id, {
        outcome: outcomeValue,
        outcome_note: outcomeNote.trim() || null,
      });
      await load();
      setLogs((prev) => [...prev, `已记录实际结果：${OUTCOME_LABELS[outcomeValue]}`]);
    } catch (err) {
      setError(pickErrorMessage(err, '保存实际结果失败'));
    } finally {
      setOutcomeSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!data) {
      return;
    }
    if (!window.confirm(`确定删除「${data.name}」及其评估记录吗？`)) {
      return;
    }
    try {
      await screeningApi.remove(data.id);
      onBack();
    } catch (err) {
      setError(pickErrorMessage(err, '删除失败'));
    }
  };

  if (loading && !data) {
    return <div className="py-16 text-center text-sm text-gray-500">加载中…</div>;
  }

  if (!data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-rose-600">{error || '未找到该招标信息'}</p>
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600"
        >
          返回列表
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* 头部与操作 */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">{data.name}</h2>
              <span
                title={
                  comparison?.created_at
                    ? `最近审核 ${formatDateTime(comparison.created_at)}`
                    : '尚未做过「资质对比与决策」审核'
                }
                className={`rounded-full border px-2 py-0.5 text-xs ${DECISION_CLASSES[data.decision]}`}
              >
                {BID_FIT_LABELS[data.decision]}
              </span>
              {headerScore !== null && (
                <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600">
                  {comparison ? '审核得分' : '历史得分'} {headerScore}
                  {comparison?.best_company_name ? `（${comparison.best_company_name}）` : ''}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {data.tenderer || '招标人未提供'} · {data.region || '地区未提供'} · 投标截止{' '}
              {formatDateTime(data.bid_deadline)}
              {data.source_url && (
                <>
                  {' · '}
                  <a
                    href={data.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    查看公告原文
                  </a>
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void runIngest()}
              disabled={running !== null}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {running === 'ingest' ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowPathIcon className="h-4 w-4" />
              )}
              {data.has_raw_text ? '重新解析要点' : '解析公告要点'}
            </button>
            <button
              type="button"
              onClick={() => void handleConvert()}
              disabled={converting}
              className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
            >
              <ArrowRightIcon className="h-4 w-4" />
              {data.converted_project_id ? '已转入投标编写' : '转入投标文件编写'}
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50"
            >
              <TrashIcon className="h-4 w-4" />
              删除
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        {logs.length > 0 && (
          <div className="mt-3 max-h-28 overflow-y-auto rounded-lg bg-gray-900/90 px-3 py-2 font-mono text-xs leading-relaxed text-gray-100">
            {logs.map((log, index) => (
              <p key={`${log}-${index}`}>{log}</p>
            ))}
          </div>
        )}
      </div>

      {/* 批量初筛结论 */}
      {data.prescreen_status !== 'none' && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">批量初筛结论</h3>
            <span
              className={`rounded-full border px-2 py-0.5 text-xs ${PRESCREEN_CLASSES[data.prescreen_status]}`}
            >
              {PRESCREEN_LABELS[data.prescreen_status]}
              {data.prescreen_score !== null ? ` ${data.prescreen_score} 分` : ''}
            </span>
          </div>
          {data.prescreen_reason && (
            <p className="mt-2 text-sm leading-relaxed text-gray-700">{data.prescreen_reason}</p>
          )}
          <p className="mt-1.5 text-xs text-gray-500">
            初筛只做方向性判断，用于决定是否值得细评；最终结论以下方「资质对比与决策」为准。
          </p>
        </div>
      )}

      {/* 公告要点 */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">公告要点</h3>
        <div className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['招标人', data.tenderer],
            ['招标代理', data.agency],
            ['项目地区', data.region],
            ['行业类别', data.industry],
            ['预算金额', data.budget],
            ['资金来源', data.budget_source],
            ['工期/服务期', data.duration],
            ['投标保证金', data.deposit],
            ['投标截止', formatDateTime(data.bid_deadline)],
            ['开标时间', formatDateTime(data.open_time)],
            [
              '价格分',
              data.price_score ? `${data.price_score} 分` : null,
            ],
            ['来源', data.source_site || data.source_url],
            ['录入方式', data.source_type],
          ].map(([label, value]) => (
            <div key={label as string} className="flex gap-2">
              <span className="shrink-0 text-gray-500">{label}：</span>
              <span className="text-gray-800">{value || '未提供'}</span>
            </div>
          ))}
        </div>

        {(data.qualification_summary || data.scoring_summary) && (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {data.qualification_summary && (
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-500">资格条件摘要</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {data.qualification_summary}
                </p>
              </div>
            )}
            {data.scoring_summary && (
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-500">评分办法摘要</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {data.scoring_summary}
                </p>
              </div>
            )}
          </div>
        )}

        {data.attachments && data.attachments.length > 0 && (
          <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <p className="text-xs font-medium text-gray-500">
              公告附件（已纳入要点抽取）
              {data.attachments.filter((item) => item.ok).length > 0 &&
                ` · 成功 ${data.attachments.filter((item) => item.ok).length} 个`}
            </p>
            <ul className="mt-1.5 space-y-1">
              {data.attachments.map((item) => (
                <li key={item.url} className="flex flex-wrap items-center gap-1.5 text-xs">
                  <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="max-w-md truncate text-gray-700">{item.name}</span>
                  {item.ok ? (
                    <span className="text-emerald-600">{item.char_count} 字</span>
                  ) : (
                    <span className="text-amber-600">{item.error || '未能提取文本'}</span>
                  )}
                  <span className="text-gray-300">|</span>
                  <span className="text-gray-400">{item.file_type}</span>
                </li>
              ))}
            </ul>
            {data.attachments.some((item) => !item.ok) && (
              <p className="mt-1.5 text-xs text-amber-700">
                部分附件需要登录或为扫描件，未能自动解析；若其中含资格条件，请在下方逐条判定中人工确认。
              </p>
            )}
          </div>
        )}

        {data.raw_text && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowRaw((prev) => !prev)}
              className="text-xs text-indigo-600 hover:underline"
            >
              {showRaw ? '收起公告全文' : '查看公告全文'}
            </button>
            {showRaw && (
              <pre className="mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-700">
                {data.raw_text}
              </pre>
            )}
          </div>
        )}
      </div>

      {/* 历史单企业评审记录（单企业入口已下线，结论统一由下方「资质对比与决策」给出） */}
      {evaluation && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">
                历史单企业评审记录（已停用 · 旧口径）
              </h3>
              <p className="mt-0.5 text-xs text-gray-500">
                单企业「资质比对与决策」已下线：结论统一由下方「资质对比与决策」给出，只选 1 家即为单企业结论。
                此处仅保留历史记录以便追溯（旧口径的「建议投 / 建议不投」与上方「适合投标」是同一件事）。
              </p>
            </div>
            <span className="shrink-0 text-xs text-gray-400">
              第 {evaluation.version} 次评估
              {evaluation.created_at ? ` · ${formatDateTime(evaluation.created_at)}` : ''}
              {evaluation.model_name ? ` · ${evaluation.model_name}` : ''}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span
              className={`rounded-lg border px-3 py-1 text-sm font-medium ${DECISION_CLASSES[evaluation.conclusion]}`}
            >
              {DECISION_LABELS[evaluation.conclusion]}
            </span>
            <span className="text-sm text-gray-600">
              客观分预估失分{' '}
              <span className="font-medium text-rose-600">{evaluation.objective_lost_score}</span>
              {evaluation.objective_max_score > 0 && (
                <span className="text-gray-400"> / 满分 {evaluation.objective_max_score}</span>
              )}{' '}
              分
            </span>
            <span className="text-xs text-gray-400">预计总分 {evaluation.total_score} / 100</span>
            {evaluation.summary_md && (
              <button
                type="button"
                onClick={() => setShowLegacyEvaluation((prev) => !prev)}
                className="text-xs text-indigo-600 hover:underline"
              >
                {showLegacyEvaluation ? '收起历史结论说明' : '查看历史结论说明'}
              </button>
            )}
          </div>

          {showLegacyEvaluation && evaluation.summary_md && (
            <p className="mt-3 whitespace-pre-wrap rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm leading-relaxed text-gray-700">
              {evaluation.summary_md}
            </p>
          )}
        </div>
      )}

      {/* 资质对比与决策（用未过滤的总条数，避免受上方筛选影响） */}
      <CompanyComparison
        opportunityId={data.id}
        requirementCount={mandatorySummary.total}
        onCompleted={() => {
          // 审核已按最新的人工确认判定重跑，清掉「需重新执行」的提示
          setVerdictNotice(null);
          void load();
        }}
      />

      {/* 实际结果回流 */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">实际结果（用于决策复盘）</h3>
          {data.outcome !== 'unknown' && (
            <span
              className={`rounded-full border px-2 py-0.5 text-xs ${OUTCOME_CLASSES[data.outcome]}`}
            >
              {OUTCOME_LABELS[data.outcome]}
              {data.outcome_at ? ` · ${formatDateTime(data.outcome_at)}` : ''}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          招标结束后记录实际结果，系统会统计「模型建议 vs 实际结果」的命中情况，用于校准判定口径。
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={outcomeValue}
            onChange={(event) => setOutcomeValue(event.target.value as ScreeningOutcome)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400"
          >
            {OUTCOME_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {OUTCOME_LABELS[option]}
              </option>
            ))}
          </select>
          <input
            value={outcomeNote}
            onChange={(event) => setOutcomeNote(event.target.value)}
            maxLength={500}
            placeholder="备注（如中标单位、落标原因）"
            className="min-w-[240px] flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
          />
          <button
            type="button"
            onClick={() => void handleSaveOutcome()}
            disabled={outcomeSaving}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
          >
            {outcomeSaving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            保存结果
          </button>
        </div>
      </div>

      {/* 公告要求与判定（公告级：要求/硬性门槛/分值/原文依据，对所有参评企业一致） */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">公告要求清单（公告级）</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              只保留公告级内容：要求文本、是否硬性门槛、分值与公告原文依据对所有参评企业一致，共{' '}
              {mandatorySummary.total} 条（其中硬性门槛 {mandatorySummary.mandatory} 条）；
              某家企业是否满足某条，请在下方「资质对比与决策」按企业逐条确认（判定与得分口径以那里为准）
            </p>
          </div>
          <div className="flex gap-1">
            {[
              { key: 'all' as const, label: '全部' },
              { key: 'mandatory' as const, label: '硬性门槛' },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`rounded-lg px-2.5 py-1 text-xs transition ${
                  filter === item.key
                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {verdictNotice && (
          <p className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            {verdictNotice}
          </p>
        )}

        <div className="divide-y divide-gray-100">
          {requirements.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-gray-500">暂无要求条目</p>
          )}
          {requirements.map((item) => {
            const expanded = expandedId === item.id;
            return (
              <div key={item.id} className="px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-400">#{item.seq}</span>
                      <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600">
                        {CATEGORY_LABELS[item.category]}
                      </span>
                      {item.is_mandatory && (
                        <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                          硬性门槛
                        </span>
                      )}
                      {!item.is_mandatory && item.score_weight > 0 && (
                        <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                          分值 {item.score_weight}
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-gray-800">{item.requirement}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : item.id)}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
                    >
                      {expanded ? (
                        <ChevronUpIcon className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDownIcon className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="mt-2 space-y-2 rounded-lg bg-gray-50 p-3">
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={item.is_mandatory}
                        onChange={(event) =>
                          void handleMandatoryToggle(item.id, event.target.checked)
                        }
                      />
                      标记为硬性门槛（不满足则不建议投标）
                    </label>
                    <div>
                      <p className="text-xs font-medium text-gray-500">公告原文依据</p>
                      <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-gray-600">
                        {item.source_quote || '（模型未提供原文片段）'}
                      </p>
                    </div>
                    {item.final_verdict && (
                      <p className="text-xs text-amber-700">
                        历史公告级人工确认（{VERDICT_LABELS[item.final_verdict]}）已迁移为按企业确认，
                        不再参与审核
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
